/**
 * PermitsBoardTab — the Permitting workspace's cross-project Permits board (2026-09).
 *
 * Read + navigate only — no logging activity, adding a permit, etc. That editing stays exactly
 * where it is today (the per-project Reference > Permits section, PermitsSection.jsx). This tab
 * broadens the /permits command's "who has the ball" triage picker into a real, always-visible
 * dashboard: grouped by project, status + ball indicator, days-until-deadline. Reuses
 * services/permits.js#getAllAgencyReviews and the exact navigate-into-project logic
 * commandExecutor.js#selectPickerItem already uses for the /permits picker's own selection.
 *
 * See docs/PERMITTING_WORKSPACE.md.
 */
import { useEffect, useState } from 'react'
import { getAllAgencyReviews } from '../services/permits.js'
import { ctx } from '../outline/ctx.js'
import { ReviewClockChip } from './ReviewClock.jsx'
import { loadReviewClocks, shouldPromptClock } from '../services/reviewClock.js'

const fmtDate = s => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s || ''); return m ? `${m[2]}/${m[3]}/${m[1]}` : '' }

function openReview(review) {
  ctx.closeAllWorkspaces()
  ctx.zoomToProject(review.projectId)
  if (ctx.zoomToRoot) setTimeout(() => ctx.zoomToRoot('permits'), 0)
}

export default function PermitsBoardTab() {
  const [ballOnly, setBallOnly] = useState(true)
  const [reviews, setReviews] = useState(null)

  useEffect(() => {
    setReviews(null)
    getAllAgencyReviews({ ballOnly }).then(setReviews).catch(() => setReviews([]))
  }, [ballOnly])
  useEffect(() => { loadReviewClocks().catch(() => {}) }, [])

  const sorted = (reviews || []).filter(r => r.projectId).slice().sort((a, b) => {
    const da = a.daysUntilDeadline, db = b.daysUntilDeadline
    if (da == null && db == null) return 0
    if (da == null) return 1
    if (db == null) return -1
    return da - db
  })

  const byProject = new Map()
  sorted.forEach(r => {
    const key = r.projectId
    if (!byProject.has(key)) byProject.set(key, { projectName: r.projectName, projectNumber: r.projectNumber, reviews: [] })
    byProject.get(key).reviews.push(r)
  })

  const S = STYLES
  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <div style={S.title}>{ballOnly ? 'Where we have the ball' : 'All agency reviews'}</div>
        <button style={S.toggleBtn} onClick={() => setBallOnly(b => !b)}>
          {ballOnly ? 'Show all reviews' : 'Show only where we have the ball'}
        </button>
      </div>

      {reviews == null && <div style={S.muted}>Loading…</div>}
      {reviews && byProject.size === 0 && <div style={S.muted}>Nothing to show.</div>}

      {Array.from(byProject.values()).map(group => (
        <div key={group.projectNumber + group.projectName} style={S.group}>
          <div style={S.groupTitle}>{[group.projectNumber, group.projectName].filter(Boolean).join(' — ')}</div>
          {group.reviews.map(r => {
            const overdue = r.daysUntilDeadline != null && r.daysUntilDeadline < 0
            return (
              <button key={r.id} style={S.row} onClick={() => openReview(r)}>
                <span style={{ ...S.ballDot, opacity: r.hasBall ? 1 : 0.15 }}>⚽</span>
                <span style={S.rowMain}>{r.agency || r.name}</span>
                <ReviewClockChip reviewId={r.id} reviewName={r.agency || r.name}
                  prompt={shouldPromptClock(r)} />
                <span style={S.rowStatus}>{r.status || 'New'}</span>
                <span style={{ ...S.rowDeadline, color: overdue ? '#c0392b' : 'var(--aj-text-muted)' }}>
                  {r.nextDeadline ? (overdue ? 'overdue · ' : 'due ') + fmtDate(r.nextDeadline) : ''}
                </span>
              </button>
            )
          })}
        </div>
      ))}
    </div>
  )
}

const STYLES = {
  wrap: { padding: '18px 24px', fontFamily: '"DM Sans",sans-serif', maxWidth: 900 },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
  title: { fontSize: 15, fontWeight: 600, color: 'var(--aj-text)' },
  toggleBtn: { border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-elevated)', color: 'var(--aj-text)', cursor: 'pointer', borderRadius: 4, padding: '6px 12px', fontSize: 12, fontFamily: '"DM Sans",sans-serif' },
  muted: { color: 'var(--aj-text-muted)', fontSize: 13, padding: '8px 0' },
  group: { marginBottom: 18 },
  groupTitle: { fontSize: 12, fontWeight: 600, color: 'var(--aj-text-mid)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' },
  row: { display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', border: 'none', borderBottom: '0.5px solid var(--aj-border)', background: 'transparent', cursor: 'pointer', padding: '8px 4px', fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)' },
  ballDot: { fontSize: 13, flexShrink: 0 },
  rowMain: { flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  rowStatus: { fontSize: 11, color: 'var(--aj-text-mid)', flexShrink: 0 },
  rowDeadline: { fontFamily: '"DM Mono",monospace', fontSize: 11, flexShrink: 0, width: 130, textAlign: 'right' },
}
